import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { sendSignup } from '../lib/emailjs'

type View = 'form' | 'submitting' | 'thankyou' | 'error'

const SIZES = ['<25', '25-100', '101-500', '501-2000', '2001+'] as const
type Size = (typeof SIZES)[number]

export default function SignupModal({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [view, setView] = useState<View>('form')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [company, setCompany] = useState('')
  const [size, setSize] = useState<Size | null>(null)
  const [email, setEmail] = useState('')
  const [useCase, setUseCase] = useState('')

  useEffect(() => {
    if (open) {
      setView('form')
      setFirstName('')
      setLastName('')
      setCompany('')
      setSize(null)
      setEmail('')
      setUseCase('')
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [open, onClose])

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }
  }, [open])

  const canSubmit = Boolean(
    firstName.trim() &&
      lastName.trim() &&
      company.trim() &&
      size &&
      email.trim() &&
      useCase.trim()
  )

  const submit = async () => {
    if (!canSubmit) return
    setView('submitting')
    try {
      await sendSignup({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        company: company.trim(),
        size: size ?? '',
        use_case: useCase.trim(),
      })
      setView('thankyou')
    } catch (err) {
      console.error('Signup submit failed:', err)
      setView('error')
    }
  }

  const inputCls =
    'w-full rounded-xl border border-oak-black/10 bg-white px-4 py-3 text-[15px] tracking-[-0.2px] text-oak-black placeholder:text-oak-black/30 focus:border-oak-black/25 focus:outline-none transition-colors'

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.38, 0.005, 0.215, 1] as const }}
          className="fixed inset-0 z-[10010] flex flex-col overflow-y-auto bg-oak-butter"
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="fixed right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-oak-black/5 text-oak-black transition-colors hover:bg-oak-black/10 md:right-8 md:top-8 md:h-12 md:w-12"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          {view === 'form' && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.38, 0.005, 0.215, 1] as const, delay: 0.1 }}
              className="mx-auto flex w-full max-w-[720px] flex-1 flex-col px-6 pb-16 pt-24 md:pt-32"
            >
              <h2 className="font-serif text-[clamp(40px,6vw,72px)] leading-[0.95] tracking-[-1.5px] text-oak-black">
                Tell us a bit about yourself
              </h2>
              <p className="mt-5 max-w-[520px] text-[clamp(15px,1.5vw,17px)] leading-[1.5] tracking-[-0.2px] text-oak-black/55">
                We&apos;ll use this to tailor your onboarding. See our{' '}
                <a
                  href="/privacy"
                  onClick={onClose}
                  className="underline decoration-oak-black/20 underline-offset-2 transition-colors hover:text-oak-black/70"
                >
                  Privacy Policy
                </a>
                .
              </p>

              <div className="mt-10 md:mt-12">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <label className="block text-[12px] font-semibold text-oak-black/60">
                          First name *
                        </label>
                        <input
                          className={`${inputCls} mt-1.5`}
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-[12px] font-semibold text-oak-black/60">
                          Last name *
                        </label>
                        <input
                          className={`${inputCls} mt-1.5`}
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="block text-[12px] font-semibold text-oak-black/60">
                        Company or organization *
                      </label>
                      <input
                        className={`${inputCls} mt-1.5`}
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                      />
                    </div>

                    <div className="mt-3">
                      <label className="block text-[12px] font-semibold text-oak-black/60">
                        Company size *
                      </label>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {SIZES.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => setSize(s)}
                            className={`rounded-full border px-4 py-2 text-[14px] font-medium tracking-[-0.2px] transition-all ${
                              size === s
                                ? 'border-oak-black bg-oak-black text-white'
                                : 'border-oak-black/10 bg-white text-oak-black hover:border-oak-black/25 hover:bg-oak-cream'
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-3">
                      <label className="block text-[12px] font-semibold text-oak-black/60">
                        Work email *
                      </label>
                      <input
                        type="email"
                        className={`${inputCls} mt-1.5`}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>

                    <div className="mt-3">
                      <label className="block text-[12px] font-semibold text-oak-black/60">
                        How will you use Oak? *
                      </label>
                      <textarea
                        rows={3}
                        className={`${inputCls} mt-1.5 resize-none !rounded-2xl`}
                        value={useCase}
                        onChange={(e) => setUseCase(e.target.value)}
                        placeholder="Tell us a bit about your community or marketplace..."
                      />
                    </div>

                    <button
                      type="button"
                      onClick={submit}
                      disabled={!canSubmit}
                      className="mt-6 flex w-full items-center justify-center gap-3 rounded-pill bg-oak-black py-4 text-[16px] font-medium tracking-[-0.4px] text-white transition-opacity hover:opacity-80 disabled:cursor-default disabled:opacity-30"
                    >
                      Get started
                      <span className="h-7 w-7 rounded-full bg-oak-green" />
                    </button>
                </div>
              </motion.div>
              )}

              {view === 'submitting' && (
                <motion.div
                  key="spin"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-1 flex-col items-center justify-center px-6 py-20"
                >
                  <div className="h-10 w-10 animate-spin rounded-full border-[2.5px] border-oak-black/10 border-t-oak-black" />
                  <p className="mt-5 text-[16px] font-medium text-oak-black/40">
                    Getting you set up…
                  </p>
                </motion.div>
              )}

              {view === 'thankyou' && (
                <motion.div
                  key="ty"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.38, 0.005, 0.215, 1] as const }}
                  className="flex flex-1 flex-col items-center justify-center px-6 py-20 text-center"
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-oak-green/15">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M20 6L9 17l-5-5"
                        stroke="#59B744"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h2 className="mt-6 font-serif text-[clamp(32px,5vw,56px)] leading-[0.95] tracking-[-1.2px] text-oak-black">
                    You&apos;re all set
                  </h2>
                  <p className="mt-4 max-w-[420px] text-[clamp(15px,1.5vw,17px)] leading-[1.5] text-oak-black/55">
                    We&apos;ll reach out to{' '}
                    <span className="font-medium text-oak-black/70">{email}</span>{' '}
                    with next steps.
                  </p>
                  <button
                    type="button"
                    onClick={onClose}
                    className="mt-10 inline-flex items-center gap-3 rounded-pill bg-oak-black px-7 py-4 text-[16px] font-medium tracking-[-0.4px] text-white transition-opacity hover:opacity-80"
                  >
                    Close
                    <span className="h-7 w-7 rounded-full bg-oak-green" />
                  </button>
                </motion.div>
              )}

              {view === 'error' && (
                <motion.div
                  key="err"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.38, 0.005, 0.215, 1] as const }}
                  className="flex flex-1 flex-col items-center justify-center px-6 py-20 text-center"
                >
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/10">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9.5" stroke="#dc2626" strokeWidth="2" />
                      <path d="M12 7.5v5.5M12 16.5h.01" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h2 className="mt-6 font-serif text-[clamp(32px,5vw,56px)] leading-[0.95] tracking-[-1.2px] text-oak-black">
                    Something went wrong
                  </h2>
                  <p className="mt-4 max-w-[440px] text-[clamp(15px,1.5vw,17px)] leading-[1.5] text-oak-black/55">
                    We couldn&apos;t send your info. Try again, or email us at{' '}
                    <a href="mailto:info@oakplatforms.com" className="font-medium text-oak-black/70 underline underline-offset-2">info@oakplatforms.com</a>.
                  </p>
                  <div className="mt-10 flex flex-wrap justify-center gap-3">
                    <button
                      type="button"
                      onClick={submit}
                      className="inline-flex items-center gap-3 rounded-pill bg-oak-black px-7 py-4 text-[16px] font-medium tracking-[-0.4px] text-white transition-opacity hover:opacity-80"
                    >
                      Try again
                      <span className="h-7 w-7 rounded-full bg-oak-green" />
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-pill border border-oak-black/15 bg-white px-7 py-4 text-[16px] font-medium tracking-[-0.4px] text-oak-black transition-colors hover:bg-white/70"
                    >
                      Close
                    </button>
                  </div>
                </motion.div>
              )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
